import { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimationControls } from "framer-motion";

const TICKER_TEXT = "What's Hot in Dallas This Month — Get the Scoop! 🔥 🔥 🔥";

export default function NewsTicker() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const controls = useAnimationControls();

  // Touch/swipe tracking
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swiped = useRef(false);

  // Start the scrolling animation
  useEffect(() => {
    controls.start({
      x: ["0%", "-50%"],
      transition: {
        x: {
          repeat: Infinity,
          repeatType: "loop",
          duration: 18,
          ease: "linear",
        },
      },
    });
  }, [controls]);

  // Pause / resume based on hover state
  useEffect(() => {
    if (paused) {
      controls.stop();
    } else {
      controls.start({
        x: ["0%", "-50%"],
        transition: {
          x: {
            repeat: Infinity,
            repeatType: "loop",
            duration: 18,
            ease: "linear",
          },
        },
      });
    }
  }, [paused, controls]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    swiped.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      // Only treat as swipe if horizontal movement > vertical and > 30px
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 30) {
        swiped.current = true;
      }
      // If not a swipe, treat as tap → navigate
      if (!swiped.current) {
        navigate("/dallas-monthly-scoop");
      }
    },
    [navigate]
  );

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden cursor-pointer select-none"
      style={{
        paddingTop: "5px",
        paddingBottom: "5px",
        background: "linear-gradient(90deg, #082f6d 0%, #51ba4f 35%, #f59e0b 65%, #082f6d 100%)",
        borderBottom: "2px solid #f59e0b",
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={() => navigate("/dallas-monthly-scoop")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate("/dallas-monthly-scoop");
        }
      }}
      aria-label="View Dallas Monthly Scoop"
    >
      {/* Subtle shimmer overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 50%, rgba(255,255,255,0.04) 100%)",
        }}
      />

      {/* Scrolling track */}
      <div className="flex items-center h-full whitespace-nowrap">
        <motion.div
          className="flex items-center gap-6"
          animate={controls}
        >
          {[...Array(4)].map((_, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-2 text-[15px] font-bold tracking-wide"
              style={{
                fontFamily: "'Poppins', sans-serif",
                color: "#FFFFFF",
                textShadow: "0 1px 2px rgba(0,0,0,0.25)",
              }}
            >
              <span>{TICKER_TEXT}</span>
              <span
                className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: "#f59e0b" }}
              />
            </span>
          ))}
        </motion.div>
      </div>

      {/* Subtle edge fades */}
      <div
        className="absolute left-0 top-0 bottom-0 w-8 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, #082f6d 0%, transparent 100%)",
        }}
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none"
        style={{
          background: "linear-gradient(270deg, #082f6d 0%, transparent 100%)",
        }}
      />
    </div>
  );
}
